import { FC, useRef, useState } from 'react';
import {
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

import { Rooftop } from '@libs/models';
import { useRooftopAuthorizationCreateMutation } from '@carcareers/store';
import { useSnackbar } from '@carcareers/contexts';

interface AddAuthorizedEmployerModalProps {
  open: boolean;
  onClose: () => void;
  rooftop?: Rooftop;
  onSuccess?: () => void;
}

export const AddAuthorizedEmployerModal: FC<AddAuthorizedEmployerModalProps> = ({ open, onClose, rooftop, onSuccess }) => {
  const { toggleSnackbar } = useSnackbar();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [createAuthorization] = useRooftopAuthorizationCreateMutation();

  const handleAddEmployer = async () => {
    if (!rooftop?.id) return;
    setIsSubmitting(true);

    try {
      await createAuthorization({
        rooftopId: rooftop?.id.toString() || '',
        body: { email },
      }).unwrap();

      setSuccess(true);
      if (onSuccess) {
        onSuccess();
      }
      setTimeout(() => {
        onClose();
        setEmail('');
        setSuccess(false);
      }, 1500);
    } catch (error) {
      toggleSnackbar('error', 'Failed to send invitation');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2,
          bgcolor: 'secondary.main',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAddIcon sx={{ color: 'secondary.contrastText' }} />
          <Typography variant="h5" color="secondary.contrastText">
            Invite New Employer
          </Typography>
        </Box>
        <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
          <CloseIcon sx={{ color: 'secondary.contrastText' }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Typography variant="body1" sx={{ my: 3 }}>
          Enter the email address of the employer you want to invite to this rooftop. If they are an existing
          CARCAREERS.COM employer, they will automatically be added. If not, they will receive an invitation
          email with instructions to join.
        </Typography>

        <TextField
          autoFocus
          label="Employer Email"
          type="email"
          fullWidth
          variant="outlined"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
          }}
          disabled={isSubmitting}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailIcon color="primary" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" color="text.secondary">
          The employer will be able to view and manage jobs at this rooftop after accepting the invitation.
        </Typography>
      </DialogContent>

      {!success && (
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={onClose} variant="outlined" disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleAddEmployer}
            variant="contained"
            disableElevation
            disabled={isSubmitting}
            sx={{
              px: 3,
              bgcolor: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
            }}
          >
            {isSubmitting ? 'Sending...' : 'Send Invitation'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
